import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Mail, Loader2 } from 'lucide-react';

interface SignInFormProps {
  onSuccess?: () => void;
  redirectTo?: string;
}

export function SignInForm({ onSuccess, redirectTo }: SignInFormProps) {
  const navigate = useNavigate();
  const { signIn, signInWithProvider, error: authError, loading: authLoading, clearError } = useAuth();
  const [isOAuthLoading, setIsOAuthLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formErrors, setFormErrors] = useState<{ email?: string; password?: string }>({});

  const validateForm = () => {
    const errors: { email?: string; password?: string } = {};
    
    if (!email) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Email is invalid';
    }
    
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!validateForm()) {
      return;
    }

    try {
      await signIn(email, password);
      // Call success callback immediately after successful sign in
      onSuccess?.();
    } catch (error) {
      console.error('Sign in failed:', error);
      // Error is already set by the signIn function
    }
  };

  const handleOAuthSignIn = async (provider: 'google') => {
    try {
      setIsOAuthLoading(true);
      await signInWithProvider(provider);
    } catch (error) {
      console.error('OAuth sign in error:', error);
    } finally {
      setIsOAuthLoading(false);
    }
  };

  const isLoading = authLoading || isOAuthLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-sand-300 mb-1">
          Email
        </label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          className="w-full px-3 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded-none focus:outline-none focus:ring-1 focus:ring-sand-400 disabled:opacity-50"
          placeholder="you@example.com"
          disabled={isLoading}
        />
        {formErrors.email && (
          <p className="mt-1 text-sm text-red-500">{formErrors.email}</p>
        )}
      </div>
      
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-sand-300 mb-1">
          Password
        </label>
        <input
          type="password"
          id="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          className="w-full px-3 py-2 bg-earth-800 border border-earth-700 text-sand-100 rounded-none focus:outline-none focus:ring-1 focus:ring-sand-400 disabled:opacity-50"
          placeholder="••••••••"
          disabled={isLoading}
        />
        {formErrors.password && (
          <p className="mt-1 text-sm text-red-500">{formErrors.password}</p>
        )}
      </div>

      {authError && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-sm text-red-500 text-center">{authError}</p>
        </div>
      )}

      <Button 
        type="submit" 
        className="w-full" 
        disabled={isLoading}
      >
        {isLoading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Logging in...
          </>
        ) : (
          'Login'
        )}
      </Button>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-earth-700"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-earth-800 text-sand-400">Or continue with</span>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={() => handleOAuthSignIn('google')}
        className="w-full"
        disabled={isLoading}
      >
        {isOAuthLoading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Connecting...
          </>
        ) : (
          <>
            <Mail className="h-4 w-4 mr-2" />
            Google
          </>
        )}
      </Button>
    </form>
  );
}